/**
 * Catálogo de uso do produto (métricas do sistema no Master, 03/11/2026).
 * O app registra telas abertas pelo PADRÃO da rota (ex.: /app/leads/:id), nunca IDs, e algumas ações de interface.
 * A API só aceita nomes deste catálogo; o Master usa o mesmo catálogo para rótulos e para achar telas sem uso.
 */

export type UsagePanel = "app" | "admin";

export interface UsageScreen {
  pattern: string;
  label: string;
  area: string;
  panel: UsagePanel;
}

const s = (panel: UsagePanel, area: string, pattern: string, label: string): UsageScreen => ({ panel, area, pattern, label });

export const USAGE_SCREENS: UsageScreen[] = [
  s("app", "Painel", "/onboarding", "Onboarding (guia)"),
  s("app", "Painel", "/app/overview", "Visão geral"),
  s("app", "Painel", "/app/organization", "Visão da organização"),
  s("app", "Painel", "/app/notifications", "Notificações"),
  s("app", "Painel", "/app/guide", "Guia de configuração"),
  s("app", "Painel", "/app/guide/start", "Guia — Passo 0"),
  s("app", "Comercial", "/app/leads", "Leads"),
  s("app", "Comercial", "/app/leads/:id", "Detalhe do lead"),
  s("app", "Comercial", "/app/opportunities", "Oportunidades"),
  s("app", "Comercial", "/app/revenue", "Receita"),
  s("app", "Marketing", "/app/acquisition", "Campanhas"),
  s("app", "Marketing", "/app/creatives", "Criativos"),
  s("app", "Marketing", "/app/attribution", "Atribuição"),
  s("app", "Marketing", "/app/planning", "Planejamento"),
  s("app", "Marketing", "/app/reports", "Relatórios"),
  s("app", "Ferramentas", "/app/integrations/meta", "Meta Ads"),
  s("app", "Ferramentas", "/app/integrations", "Integrações"),
  s("app", "Ferramentas", "/app/integrations/:id", "Detalhe da integração"),
  s("app", "Ferramentas", "/app/automations", "Automações"),
  s("app", "Ferramentas", "/app/automations/:id", "Editor de automação"),
  s("app", "Ferramentas", "/app/forms", "Formulários"),
  s("app", "Ferramentas", "/app/audiences", "Públicos"),
  s("app", "Ferramentas", "/app/tracking/sites", "Pixel e sites"),
  s("app", "Ferramentas", "/app/tracking/sites/:id", "Detalhe do site"),
  s("app", "Ferramentas", "/app/tracking/links", "Links"),
  s("app", "Ferramentas", "/app/tracking/links/:id", "Detalhe do link"),
  s("app", "Ferramentas", "/app/tracking/events", "Eventos"),
  s("app", "Configurações", "/app/settings", "Configurações — Geral"),
  s("app", "Configurações", "/app/team", "Equipe"),
  s("app", "Configurações", "/app/settings/workspaces", "Workspaces"),
  s("app", "Comercial", "/app/pipelines", "Pipelines"),
  s("app", "Configurações", "/app/settings/custom-fields", "Campos personalizados"),
  s("app", "Configurações", "/app/settings/inside-sales", "Painel de vendas"),
  s("app", "Configurações", "/app/developers", "Desenvolvedores"),
  s("app", "Configurações", "/app/settings/support", "Acesso de suporte"),
  s("app", "Conta", "/app/billing", "Assinatura"),
  s("app", "Conta", "/app/partner", "Partner"),
  s("app", "Conta", "/app/feedback", "Suporte e sugestões"),
  s("app", "Conta", "/app/feedback/:id", "Chamado (conversa)"),
  s("admin", "Admin da marca", "/admin/overview", "Admin — Visão geral"),
  s("admin", "Admin da marca", "/admin/organizations", "Admin — Organizações"),
  s("admin", "Admin da marca", "/admin/organizations/:id", "Admin — Página da organização"),
  s("admin", "Admin da marca", "/admin/plans", "Admin — Planos e preços"),
  s("admin", "Admin da marca", "/admin/billing", "Admin — Cobrança"),
  s("admin", "Admin da marca", "/admin/partners", "Admin — Partners"),
  s("admin", "Admin da marca", "/admin/branding", "Admin — Marca e domínio"),
  s("admin", "Admin da marca", "/admin/email", "Admin — E-mail"),
  s("admin", "Admin da marca", "/admin/team", "Admin — Equipe"),
  s("admin", "Admin da marca", "/admin/feedback", "Admin — Chamados"),
  s("admin", "Admin da marca", "/admin/feedback/:id", "Admin — Chamado (conversa)"),
  s("admin", "Admin da marca", "/admin/ai", "Admin — IA e créditos"),
];

/** Ações de interface que o servidor não enxerga (o resto vem da auditoria das operações). */
export const USAGE_ACTIONS: Record<string, string> = {
  "ai.panel_open": "Abriu o painel da IA",
  "ai.ask": "Perguntou à IA",
  "overview.model": "Trocou o modelo da Visão geral",
  "overview.filter": "Mudou o período/filtro da Visão geral",
  "guide.overlay_go": "Usou o cartão do guia (ir para a tela)",
  "guide.overlay_check": "Usou o cartão do guia (Já fiz, conferir)",
  "theme.toggle": "Trocou tema claro/escuro",
  "workspace.switch": "Trocou de workspace",
  "ai_credits.recharge_open": "Abriu a recarga de créditos de IA",
};

const SCREEN_BY_PATTERN = new Map(USAGE_SCREENS.map((x) => [x.pattern, x]));

export function usageScreen(pattern: string): UsageScreen | null {
  return SCREEN_BY_PATTERN.get(pattern) ?? null;
}

export function isUsageName(kind: "view" | "time" | "action", name: string): boolean {
  return kind !== "action" ? SCREEN_BY_PATTERN.has(name) : Object.prototype.hasOwnProperty.call(USAGE_ACTIONS, name);
}

/** Converte um caminho real no padrão do catálogo (ex.: /app/leads/4f…  → /app/leads/:id). */
export function usagePatternFor(pathname: string): string | null {
  const clean = pathname.replace(/\/+$/, "") || "/";
  if (SCREEN_BY_PATTERN.has(clean)) return clean;
  const parts = clean.split("/");
  for (const sc of USAGE_SCREENS) {
    const pp = sc.pattern.split("/");
    if (pp.length !== parts.length) continue;
    if (pp.every((seg, i) => (seg.startsWith(":") ? parts[i]!.length > 0 : seg === parts[i]))) return sc.pattern;
  }
  return null;
}

/** Rótulos das ações registradas na auditoria (prefixo → área legível). Ações sem rótulo aparecem pelo código. */
export const AUDIT_ACTION_AREAS: Array<[prefix: string, label: string]> = [
  ["automation", "Automações"],
  ["integration", "Integrações"],
  ["meta_form", "Formulários"],
  ["meta", "Meta Ads"],
  ["ad_account", "Meta Ads"],
  ["campaign", "Campanhas"],
  ["attribution", "Atribuição"],
  ["conversion", "Pixel e conversões"],
  ["crm", "Integrações"],
  ["access_group", "Equipe"],
  ["ai_credits", "Créditos de IA"],
  ["ai", "IA"],
  ["brand", "Admin da marca"],
  ["domain", "Admin da marca"],
  ["organization", "Admin da marca"],
  ["channel", "Canais"],
  ["pipeline", "Pipelines"],
  ["stage", "Pipelines"],
  ["opportunity", "Oportunidades"],
  ["leads", "Leads"],
  ["lead", "Leads"],
  ["contact", "Leads"],
  ["sale", "Receita"],
  ["payment", "Receita"],
  ["tracking", "Rastreamento"],
  ["link", "Rastreamento"],
  ["site", "Rastreamento"],
  ["form", "Formulários"],
  ["audience", "Públicos"],
  ["segment", "Públicos"],
  ["report", "Relatórios"],
  ["export", "Relatórios"],
  ["saved_view", "Relatórios"],
  ["planning", "Planejamento"],
  ["guide", "Guia de configuração"],
  ["team", "Equipe"],
  ["invitation", "Equipe"],
  ["membership", "Equipe"],
  ["workspace", "Workspaces"],
  ["custom_field", "Campos personalizados"],
  ["api_key", "Desenvolvedores"],
  ["webhook", "Desenvolvedores"],
  ["billing", "Assinatura"],
  ["subscription", "Assinatura"],
  ["partner", "Partner"],
  ["support", "Suporte"],
  ["feedback", "Suporte e sugestões"],
  ["settings", "Configurações"],
];

export function auditActionArea(action: string): string {
  const a = action.toLowerCase();
  for (const [p, label] of AUDIT_ACTION_AREAS) if (a === p || a.startsWith(`${p}.`) || a.startsWith(`${p}_`)) return label;
  return "Outros";
}
